import { DOCUMENT } from '@angular/common';
import { afterNextRender, Component, DestroyRef, inject, Injector, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { catchError, debounceTime, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ProductTableRowComponent } from '@components/product-row-component/product-table-row.component';
import { StorageUsageBarComponent } from '@components/storage-usage-bar/storage-usage-bar.component';

import { Product } from '@services/products/interfaces/product.interfaces';
import { ProductService } from '@services/products/products.service';

import { ADMIN_PRODUCTS_CREATE, ADMIN_PRODUCTS_UPDATE } from '@constants/routes.constants';

const PAGE_LIMIT = 9;
const LIST_STATE_STORAGE_KEY = 'argentus_admin_products_return';

interface ProductListRequest {
  search: string;
  page: number;
  scrollY: number | null;
}

@Component({
  selector: 'app-view-products',
  standalone: true,
  imports: [ProductTableRowComponent, StorageUsageBarComponent],
  templateUrl: './view-products.html',
  styleUrl: './view-products.css',
})
export class ViewProducts implements OnInit {
  public readonly products = signal<Product[]>([]);
  public readonly isLoading = signal(true);
  public readonly hasLoadError = signal(false);
  public readonly searchTerm = signal('');
  public readonly currentPage = signal(1);
  public readonly totalPages = signal(1);

  private readonly productsService = inject(ProductService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly searchSubject = new Subject<string>();
  private readonly requestSubject = new Subject<ProductListRequest>();

  public ngOnInit(): void {
    this.requestSubject.pipe(
      tap(() => {
        this.isLoading.set(true);
        this.hasLoadError.set(false);
      }),
      switchMap((request) => this.productsService.getAdminProducts(request.page, PAGE_LIMIT, request.search).pipe(
        takeUntil(this.searchSubject),
        catchError(() => {
          this.hasLoadError.set(true);
          return of(null);
        }),
        tap((response) => {
          if (!response) {
            this.products.set([]);
            this.isLoading.set(false);
            return;
          }
          const lastPage = Math.max(1, response.totalPages);
          if (request.page > lastPage) {
            this.currentPage.set(lastPage);
            this.requestSubject.next({ ...request, page: lastPage });
            return;
          }
          this.products.set(response.data);
          this.currentPage.set(response.page);
          this.totalPages.set(lastPage);
          this.isLoading.set(false);
          if (request.scrollY !== null) {
            afterNextRender(() => {
              this.document.defaultView?.scrollTo({ top: request.scrollY ?? 0, behavior: 'instant' });
            }, { injector: this.injector });
          }
        }),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.searchSubject.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.loadProducts());

    afterNextRender(() => {
      const savedState = this.consumeReturnState();
      this.searchTerm.set(savedState.search);
      this.currentPage.set(savedState.page);
      this.requestSubject.next(savedState);
    }, { injector: this.injector });
  }

  public onSearch(search: string): void {
    this.searchTerm.set(search);
    this.currentPage.set(1);
    this.isLoading.set(true);
    this.searchSubject.next(search);
  }

  public onPageChange(page: number): void {
    if (this.isLoading() || page < 1 || page > this.totalPages()) {
      return;
    }
    this.currentPage.set(page);
    this.loadProducts(0);
  }

  public retryLoading(): void {
    this.loadProducts();
  }

  public onAdd(): void {
    this.saveReturnState();
    this.router.navigate([ADMIN_PRODUCTS_CREATE]);
  }

  public onEdit(product: Product): void {
    this.saveReturnState();
    this.router.navigate([ADMIN_PRODUCTS_UPDATE, product.slug]);
  }

  private loadProducts(scrollY: number | null = null): void {
    this.requestSubject.next({
      search: this.searchTerm().trim(),
      page: this.currentPage(),
      scrollY,
    });
  }

  private saveReturnState(): void {
    try {
      this.document.defaultView?.sessionStorage.setItem(LIST_STATE_STORAGE_KEY, JSON.stringify({
        search: this.searchTerm(),
        page: this.currentPage(),
        scrollY: this.document.defaultView.scrollY,
      }));
    } catch {
      return;
    }
  }

  private consumeReturnState(): ProductListRequest {
    const initialState: ProductListRequest = { search: '', page: 1, scrollY: null };
    try {
      const storage = this.document.defaultView?.sessionStorage;
      const savedValue = storage?.getItem(LIST_STATE_STORAGE_KEY);
      storage?.removeItem(LIST_STATE_STORAGE_KEY);
      if (!savedValue) {
        return initialState;
      }
      const savedState: unknown = JSON.parse(savedValue);
      if (typeof savedState !== 'object' || savedState === null) {
        return initialState;
      }
      const state = savedState as Partial<ProductListRequest>;
      return {
        search: typeof state.search === 'string' ? state.search : '',
        page: typeof state.page === 'number' && Number.isSafeInteger(state.page) && state.page > 0 ? state.page : 1,
        scrollY: typeof state.scrollY === 'number' && Number.isFinite(state.scrollY) && state.scrollY >= 0 ? state.scrollY : null,
      };
    } catch {
      return initialState;
    }
  }
}
